// Shared renderer for API coverage matrices — the same field-by-field view as
// the bespoke eBay Message API reference (EbayApiReference.jsx), but data-driven
// so every other API (TRQ, Post-Order, Trading, Fulfillment, Browse) reuses it.
// Feed it a data module: { api, blurb, endpoints: [{ verb, path, fn, rw?,
// params: [{p, req, send, val}], fields: [{f, pull, db, note, badge}] }] }.
import {
  Box, Typography, Stack, Chip, Divider, Table, TableHead, TableBody, TableRow, TableCell,
} from '@mui/material'

// Status vocabulary matches EbayApiReference so the two views read identically.
const STATUS = {
  pull: { label: 'Pulling', color: 'success' },
  avail: { label: 'Available · not used', color: 'default' },
  verify: { label: 'Documented · to verify', color: 'info' },
  gate: { label: 'Built · gated off', color: 'warning' },
  avoid: { label: 'Avoided by design', color: 'error' },
}
// Write ops carry a static badge of 'gate' in the data. Their *live* status
// depends on the write flag: bind it so the matrix tells the truth (e.g. eBay
// Post-Order writes are ON in prod, so "gated off" would be wrong). writeEnabled:
// true → live on approval; false → actually gated off; null → flag unknown.
function badgeFor(s, writeEnabled) {
  if (s === 'gate') {
    if (writeEnabled === true) return { label: 'Write · live on approval', color: 'success' }
    if (writeEnabled === false) return { label: 'Built · gated off', color: 'warning' }
    return { label: 'Write · gated by flag', color: 'default' }
  }
  return STATUS[s] ?? null
}
const Badge = ({ s, writeEnabled }) => {
  const b = badgeFor(s, writeEnabled)
  return b ? <Chip size="small" label={b.label} color={b.color} variant="outlined" sx={{ ml: 1, height: 18, fontSize: 10.5 }} /> : null
}
const Mono = ({ children, dim }) => (
  <Box component="span" sx={{ fontFamily: 'monospace', fontSize: 12.5, color: dim ? 'text.disabled' : 'text.primary', fontStyle: dim ? 'italic' : 'normal' }}>
    {children}
  </Box>
)
const Yes = () => <Box component="span" sx={{ fontWeight: 700, color: 'success.main' }}>Yes</Box>
const No = () => <Box component="span" sx={{ fontWeight: 700, color: 'text.disabled' }}>No</Box>

function ParamTable({ rows, writeEnabled }) {
  if (!rows?.length) return null
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small" sx={{ mb: 2, minWidth: 560 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>Parameter</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Required</TableCell>
            <TableCell sx={{ fontWeight: 700, textAlign: 'center' }}>Sending?</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Value we send</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r, i) => (
            <TableRow key={i}>
              <TableCell sx={{ verticalAlign: 'top' }}><Mono>{r.p}</Mono></TableCell>
              <TableCell sx={{ verticalAlign: 'top', color: r.req === 'required' ? 'warning.main' : 'text.secondary', fontSize: 12, whiteSpace: 'nowrap' }}>{r.req}</TableCell>
              <TableCell sx={{ verticalAlign: 'top', textAlign: 'center' }}>
                {r.send === 'y' ? (
                  <Yes />
                ) : r.send === 'gated' ? (
                  <Box component="span" sx={{ fontWeight: 700, color: writeEnabled === true ? 'success.main' : 'text.disabled' }}>
                    {writeEnabled === true ? 'On approval' : writeEnabled === false ? 'Gated (off)' : 'Gated'}
                  </Box>
                ) : (
                  <Box component="span" sx={{ fontWeight: 700, color: 'text.disabled' }}>{r.send}</Box>
                )}
              </TableCell>
              <TableCell sx={{ verticalAlign: 'top', color: 'text.secondary', fontSize: 13 }}>{r.val}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  )
}

function FieldTable({ rows, writeEnabled }) {
  if (!rows?.length) return null
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small" sx={{ mb: 2, minWidth: 620 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>Field</TableCell>
            <TableCell sx={{ fontWeight: 700, textAlign: 'center' }}>Pulling?</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>DB column</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Notes</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r, i) => (
            <TableRow key={i}>
              <TableCell sx={{ verticalAlign: 'top' }}><Mono>{r.f}</Mono></TableCell>
              <TableCell sx={{ verticalAlign: 'top', textAlign: 'center' }}>{r.pull ? <Yes /> : <No />}</TableCell>
              <TableCell sx={{ verticalAlign: 'top' }}>{r.db ? <Mono>{r.db}</Mono> : <Mono dim>— not stored</Mono>}</TableCell>
              <TableCell sx={{ verticalAlign: 'top', color: 'text.secondary', fontSize: 13 }}>{r.note}<Badge s={r.badge} writeEnabled={writeEnabled} /></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  )
}

export default function CoverageMatrix({ data, writeEnabled = null }) {
  if (!data) return null
  const hasWrites = data.endpoints.some((ep) => ep.rw === 'write' || ep.fields?.some((f) => f.badge === 'gate'))
  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 700 }}>{data.api}</Typography>
      {data.blurb && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>{data.blurb}</Typography>}
      {hasWrites && (
        <Typography variant="body2" sx={{ mb: 1.5, color: writeEnabled === true ? 'success.main' : writeEnabled === false ? 'warning.main' : 'text.secondary' }}>
          {writeEnabled === true
            ? '● Writes ENABLED — the gated actions below execute on human approval (live flag).'
            : writeEnabled === false
              ? '○ Writes disabled — the gated actions below are built but inert (live flag).'
              : '· Write status unknown (couldn’t read the live flags).'}
        </Typography>
      )}
      <Stack direction="row" spacing={0} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
        {Object.keys(STATUS).map((k) => <Badge key={k} s={k} />)}
      </Stack>
      {data.endpoints.map((ep, i) => (
        <Box key={i} sx={{ mb: 2.5 }}>
          <Stack direction="row" spacing={1.2} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1, minWidth: 0 }}>
            <Chip size="small" label={ep.verb} color={ep.verb === 'GET' ? 'primary' : 'warning'} sx={{ fontFamily: 'monospace', fontWeight: 700 }} />
            <Box component="span" sx={{ fontFamily: 'monospace', fontWeight: 600, fontSize: 14, wordBreak: 'break-all', minWidth: 0 }}>{ep.path}</Box>
            <Box component="span" sx={{ fontFamily: 'monospace', fontSize: 12, color: 'text.disabled' }}>{ep.fn}</Box>
            {ep.rw && <Chip size="small" label={ep.rw} variant="outlined" sx={{ height: 18, fontSize: 10.5 }} />}
          </Stack>
          <ParamTable rows={ep.params} writeEnabled={writeEnabled} />
          <FieldTable rows={ep.fields} writeEnabled={writeEnabled} />
          {i < data.endpoints.length - 1 && <Divider />}
        </Box>
      ))}
    </Box>
  )
}
